import { type JSX, useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import type { Chart as HighchartsChartT } from "highcharts";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsSets } from "@similarweb/styles";
import { Banner } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import Chart from "pro-features/components/Chart/src/Chart";
import type { ILegendItem } from "app/components/React/Legends/legendsTypes";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { useKeywordLists } from "app/modules/demand-analysis/keyword-lists";
import DurationService, { type IDurationObject } from "app/services/DurationService";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";

import { Divider } from "../../../components/Divider";
import { formatNumber } from "../../../utils/numberFormatters";
import { useKeywordGroupsScatterPlotData } from "../useKeywordGroupScatterPlotData";

import { getChartConfig } from "./chartConfig";
import { getAverageValues } from "./chartDataUtils";
import { KeywordGroupsScatterPlotLegends as Legends } from "./KeywordGroupsScatterPlotLegends";
import { KeywordGroupsScatterPlotLoadingSkeleton as Loader } from "./KeywordGroupsScatterPlotLoadingSkeleton";
import { KeywordGroupsScatterPlotTitles as PlotTitles } from "./KeywordGroupsScatterPlotTitles";

const WidgetContainerWrapper = styled(WidgetContainer)`
    width: 100%;
`;

const StyledBanner = styled(Banner)`
    align-self: stretch;
`;

export const KeywordGroupsScatterPlot = (): JSX.Element => {
    const {
        params: { duration },
    } = useCurrentStateAndParams();
    const { isWindow } = DurationService.getDurationData(duration).forAPI;
    const isPeriodOverPeriod = DurationService.isPeriodOverPeriod();
    const { getKeywordListById } = useKeywordLists();
    const { data, isLoading, isError } = useKeywordGroupsScatterPlotData();
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");
    const subtitleFiltersDateValue = subtitleFilters.find((f) => f.filter === "date")?.value;
    const [selectedLegendIds, setSelectedLegendIds] = useState<Set<string | symbol>>(new Set());
    const [chartInstance, setChartInstance] = useState<HighchartsChartT | null>(null);

    const handleChartLoad = (chart: HighchartsChartT) => {
        setChartInstance(chart);
    };

    const averageValues = getAverageValues(data.list);
    const { chartData, legendItems } = useMemo(() => {
        if (data.list.length === 0) {
            return { chartData: [], legendItems: [] };
        }

        const hasVisiblePoints = data.list.some((item) => selectedLegendIds.has(item.id));

        const chartData = hasVisiblePoints
            ? Object.entries(Object.groupBy(data.list, (item) => item.groupName)).map(([groupName, plotItem]) => ({
                  name: groupName,
                  data: plotItem?.map((item) => ({
                      x: item.totalVolume,
                      y: item.volumeChange,
                      id: item.isPreviousPeriod ? `${item.id}_prev` : item.id,
                      name: item.groupName,
                      color: getKeywordListById(item.id)?.color ?? colorsSets.c.others,
                      isPreviousPeriod: item.isPreviousPeriod,
                      visible: selectedLegendIds.has(item.id),
                  })),
              }))
            : [];

        const legendItems = data.list
            .filter((item) => !item.isPreviousPeriod)
            .map((dataItem) => ({
                visible: selectedLegendIds.has(dataItem.id),
                id: dataItem.id,
                name: dataItem.groupName,
                color: getKeywordListById(dataItem.id)?.color ?? colorsSets.c.others,
                data: formatNumber(dataItem.totalVolume),
            }));

        return { chartData, legendItems };
    }, [data.list, getKeywordListById, selectedLegendIds]);

    const chartConfig = getChartConfig(
        isPeriodOverPeriod,
        chartData,
        subtitleFiltersDateValue as IDurationObject<string>,
        averageValues,
        handleChartLoad,
    );

    useEffect(() => {
        if (data.list.length > 0) {
            setSelectedLegendIds(new Set(data.list.map((item) => item.id)));
        }
    }, [data]);

    const handleLegendClick = (legend: ILegendItem) => {
        const updatedSelectedLegendIds = new Set(selectedLegendIds);

        if (updatedSelectedLegendIds.has(legend.id)) {
            updatedSelectedLegendIds.delete(legend.id);
        } else {
            updatedSelectedLegendIds.add(legend.id);
        }

        setSelectedLegendIds(updatedSelectedLegendIds);
    };

    return (
        <WidgetContainerWrapper
            automationId="search-trends-quadrant-analysis-widget"
            noData={isWindow || isError}
            subtitleFilters={subtitleFilters}
            title={translate("demand.analysis.search.trends.keywords.breakdown.scatterPlot.title")}
            tooltip={translate("demand.analysis.search.trends.keywords.breakdown.scatterPlot.tooltip")}
        >
            <Divider />
            {isLoading ? (
                <Loader />
            ) : (
                <Box padding="24px">
                    <Flex
                        rowGap="24px"
                        flexDirection="column"
                    >
                        {isPeriodOverPeriod && !data.hasDataPriorToPreviousPeriod ? (
                            <StyledBanner
                                title=""
                                CustomIcon={
                                    <SWReactIcons
                                        iconName="info"
                                        size="xs"
                                    />
                                }
                                subtitle={
                                    <Text fontVariant="body1">
                                        {translate("demand.analysis.quadrant_analysis.banner.no_yoy_data.warning")}
                                    </Text>
                                }
                            />
                        ) : null}
                        <Flex
                            flexWrap="nowrap"
                            width="full"
                            gap="24px"
                        >
                            <Box
                                position="relative"
                                flexGrow="1"
                            >
                                <Chart
                                    data={chartData}
                                    config={chartConfig}
                                    type={chartTypes.SCATTER}
                                />
                                <PlotTitles chartInstance={chartInstance} />
                            </Box>
                            <Legends
                                legendItems={legendItems}
                                onToggle={handleLegendClick}
                            />
                        </Flex>
                    </Flex>
                </Box>
            )}
        </WidgetContainerWrapper>
    );
};
